import { Icon, Typography } from '@packages/ui/shared';
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { useRestrictedDragDrop } from './step-helpers/drag-drop';
import { BrowserQRCodeReader } from '@zxing/browser';
import { Result } from '@zxing/library';
import QRCodeScanner from './step-helpers/qr-code-scanner';
import { evBatteryService } from '@web/services/singletons';
import { useFormContext } from '../battery-context';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import colors from '@packages/core/styles/colors';
import { analyticsService } from '@web/services/singletons';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

const logger = getLogger('vin-sessions');

type Step34ContentProps = {
    currentStep: number;
    targetStep: 3 | 4;
    displayQrCodeScanner: boolean;
    setDisplayQrCodeScanner: React.Dispatch<React.SetStateAction<boolean>>;
    storageKey: 'newBinQrCode' | 'oldBinQrCode';
    headingLabel: string;
    resultLabel: string;
};

const styles = StyleSheet.create({
    optionsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        margin: 'auto',
        alignItems: 'center',
        gap: '16px',
    },
});

const useStyles = createUseStyles({
    attachments: {
        position: 'relative',
        height: 237,
        width: 364,
        border: '1px solid #999DA3',
        borderRadius: '4px',
    },

    loaderCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        border: '1px solid #999DA3',
        borderRadius: 4,
        marginTop: 16,
        backgroundColor: 'white',
        gap: 12,
    },

    progressContainer: {
        position: 'relative',
        width: '100%',
        height: 8,
        backgroundColor: '#E6E6E6',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },

    progressBar: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        height: '100%',
        width: '10%',
        backgroundColor: '#007bff',
        borderRadius: 4,
        animation: '$fillBar 4s linear forwards',
    },

    iconWrapper: {
        position: 'relative',
        zIndex: 1,
        paddingLeft: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },

    '@keyframes fillBar': {
        '0%': { width: '10%' },
        '100%': { width: '100%' },
    },
});

const Step34Content: React.FC<Step34ContentProps> = ({
    currentStep,
    targetStep,
    displayQrCodeScanner,
    setDisplayQrCodeScanner,
    storageKey,
    headingLabel,
    resultLabel,
}) => {
    const classes = useStyles();

    async function checkCameraPermission() {
        if (navigator.permissions) {
            try {
                const result = await navigator.permissions.query({ name: 'camera' as any });
                return result.state;
            } catch {
                // fall through to getUserMedia
            }
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach((t) => t.stop());
            return 'granted';
        } catch (error: any) {
            if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') return 'denied';
            return 'prompt';
        }
    }

    const [displayThisStep, setDisplayThisStep] = useState(false);
    const [scannerData, setScannerData] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [fileQrValidationInProgress, setFileQrValidationInProgress] = useState<boolean>(false);
    const { abortSignalRef, signalAbort } = useAbortController();
    const qrReaderRef = useRef<BrowserQRCodeReader | null>(null);
    const uploadElementRef = useRef<HTMLInputElement | null>(null);
    const fileValidationInFlightRef = useRef<boolean>(false);

    // success icon state
    const [successIcon, setSuccessIcon] = useState<{ iconName: string; colors: keyof typeof colors }>({
        iconName: 'check-circle-hollow',
        colors: 'greenOneActive',
    });
    const successIconTimerRef = useRef<number | null>(null);

    const { data, updateData } = useFormContext();

    // Keep a stable reference to updateData so the effect below doesn't re-run due to identity changes
    const updateDataRef = useRef(updateData);
    useEffect(() => {
        updateDataRef.current = updateData;
    }, [updateData]);

    // Track the last value we wrote, to avoid redundant context writes
    const lastSavedRef = useRef<string | null>(null);

    // Initialize from context once
    useEffect(() => {
        const existing = (data as any)?.[storageKey];
        if (existing) {
            setScannerData(existing as string);
            setErrorMessage(null);
            lastSavedRef.current = existing as string;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Control visibility for this step
    useEffect(() => {
        setDisplayThisStep(currentStep === targetStep && !displayQrCodeScanner);
    }, [currentStep, targetStep, displayQrCodeScanner]);

    useEffect(() => {
        setErrorMessage('');
    }, [displayQrCodeScanner]);

    // Push into context ONLY when it actually changed
    useEffect(() => {
        if (!scannerData) return;

        if (scannerData === lastSavedRef.current) return;

        const existing = (data as any)?.[storageKey];
        if (existing === scannerData) {
            lastSavedRef.current = scannerData;
            return;
        }

        updateDataRef.current({ [storageKey]: scannerData } as any);
        lastSavedRef.current = scannerData;
    }, [scannerData, storageKey, data]);

    // After a success (scannerData set and no spinner), show green check for 3s, then switch to x-circle (black)
    useEffect(() => {
        if (successIconTimerRef.current) {
            clearTimeout(successIconTimerRef.current);
            successIconTimerRef.current = null;
        }

        if (scannerData && !fileQrValidationInProgress) {
            setSuccessIcon({ iconName: 'check-circle-hollow', colors: 'greenOneActive' });

            successIconTimerRef.current = window.setTimeout(() => {
                setSuccessIcon({ iconName: 'trash-can', colors: 'textDarkSecondary' }); // delete icon
                successIconTimerRef.current = null;
            }, 3000);
        }

        return () => {
            if (successIconTimerRef.current) {
                clearTimeout(successIconTimerRef.current);
                successIconTimerRef.current = null;
            }
        };
    }, [scannerData, fileQrValidationInProgress]);

    // ---- Helpers -------------------------------------------------------------

    function withTimeout<T>(p: Promise<T>, ms = 10000): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Remote validation timed out')), ms);
            p.then(
                (v) => {
                    clearTimeout(timer);
                    resolve(v);
                },
                (err) => {
                    clearTimeout(timer);
                    reject(err);
                }
            );
        });
    }

    const remoteQrCodeValidator = async (decodedQrCodeData: string): Promise<boolean> => {
        try {
            analyticsService.logEvent(ANALYTICS_EVENTS.WEB_SCAN_ATTEMPT, {
                source: 'vin_session',
            });
            const result = await evBatteryService.validateBin({
                binQrCode: decodedQrCodeData,
                signal: abortSignalRef.current,
                ignoreCache: true,
            });
            analyticsService.logEvent(ANALYTICS_EVENTS.WEB_PARSE_SUCCESS, {
                source: 'vin_session',
            });
            return new Promise((resolve) => setTimeout(() => resolve(result.data), 500));
        } catch (err) {
            analyticsService.logEvent(ANALYTICS_EVENTS.WEB_PARSE_SUCCESS, {
                source: 'vin_session',
            });
            return new Promise((resolve) => setTimeout(() => resolve(false), 500));
        }
    };

    const validateQRCode = (value: string): boolean => {
        if (!value?.trim()) return false;

        // Allow only uppercase alphanumeric strings of length 24 or 32
        const qrCodeRegex = /^[A-Z0-9]{24}$|^[A-Z0-9]{32}$/;

        return qrCodeRegex.test(value.trim());
    };

    function loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = url;
        });
    }

    function getReader(): BrowserQRCodeReader {
        if (!qrReaderRef.current) {
            qrReaderRef.current = new BrowserQRCodeReader();
        }
        return qrReaderRef.current;
    }

    /**
     * Clears the saved QR code both locally and in the shared form context.
     * @param opts.abort If true, abort any in-flight remote validation (default: false)
     * @param opts.blankStrategy How to clear in context: "undefined" (default) or "null" or "emptyString"
     */
    function clearSavedData(opts?: { abort?: boolean; blankStrategy?: 'undefined' | 'null' | 'emptyString' }) {
        const { abort = false, blankStrategy = 'undefined' } = opts || {};

        if (abort) {
            try {
                signalAbort?.();
            } catch {
                // ignore
            }
        }

        // Clear local states/refs
        setScannerData(null);
        setErrorMessage(null);
        setFileQrValidationInProgress(false);
        fileValidationInFlightRef.current = false;
        lastSavedRef.current = null;

        // Clear in context explicitly
        let cleared: any;
        switch (blankStrategy) {
            case 'null':
                cleared = null;
                break;
            case 'emptyString':
                cleared = '';
                break;
            default:
                cleared = undefined;
        }
        updateDataRef.current({ [storageKey]: cleared } as any);
    }

    // ------------ File upload path -------------------------------------------

    const clearFileSpinner = () => {
        setFileQrValidationInProgress(false);
        fileValidationInFlightRef.current = false;
    };

    const qrCodeFileHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (displayQrCodeScanner) return;
        e.preventDefault();

        if (fileValidationInFlightRef.current) return;
        fileValidationInFlightRef.current = true;

        setErrorMessage(null);
        setScannerData(null);

        try {
            const files = e.target.files;
            if (!files || files.length === 0) {
                setErrorMessage('No file selected.');
                return;
            }

            const file = files[0];
            if (!file.type.startsWith('image/')) {
                setErrorMessage('Please upload a valid image file.');
                return;
            }

            // Spinner starts only after sync checks pass
            setFileQrValidationInProgress(true);

            const url = URL.createObjectURL(file);
            const img = await loadImage(url);
            const reader = getReader();

            const result: Result = await reader.decodeFromImageElement(img);
            const decodedText = result.getText();

            const ok = await withTimeout(remoteQrCodeValidator(decodedText), 1000);

            if (ok && validateQRCode(decodedText)) {
                setScannerData(decodedText);
                setErrorMessage(null);
            } else {
                setScannerData(null);
                setErrorMessage('Unable to read QR code from image.Please try again');
            }
        } catch (err) {
            logger.error('Failed to decode and validate the QR code from the uploaded image', { error: err });
            setScannerData(null);
            setErrorMessage('Unable to read QR code from image.Please try again');
        } finally {
            clearFileSpinner();
            if (e?.target) e.target.value = '';
        }
    };

    // ------------ Drag & drop path -------------------------------------------

    const [dropZoneId] = useState('dropZoneId-' + (Math.floor(Math.random() * 90000) + 10000));
    const { ref } = useRestrictedDragDrop<HTMLDivElement>({
        onDragStart: () => {},
        onDrop: async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setErrorMessage(null);
            setScannerData(null);

            const files = e.dataTransfer?.files;
            if (!files || files.length === 0) {
                setErrorMessage('No file detected.');
                return;
            }

            const file = files[0];
            if (!file.type.startsWith('image/')) {
                setErrorMessage('Please drop a valid image file.');
                return;
            }

            try {
                const url = URL.createObjectURL(file);
                const img = await loadImage(url);
                const reader = getReader();

                const result: Result = await reader.decodeFromImageElement(img);
                const decodedText = result.getText();

                if (validateQRCode(decodedText)) {
                    setScannerData(decodedText);
                    setErrorMessage(null);
                } else {
                    setScannerData(null);
                    setErrorMessage('Invalid QR code detected.');
                }
            } catch (err) {
                logger.error('Failed to decode the QR code from the dropped image', { error: err });
                setScannerData(null);
                setErrorMessage('Unable to read QR code from image.Please try again');
            }
        },
    });

    // ------------ Scanner modal for this step --------------------------------

    if (displayQrCodeScanner && currentStep === targetStep)
        return (
            <QRCodeScanner
                onSuccessScanCallback={(scanned) => {
                    setScannerData(scanned);
                    setDisplayQrCodeScanner(false);
                    setErrorMessage('');
                }}
                onErrorScanCallback={(errMsg) => {
                    setErrorMessage(errMsg);
                }}
                scannedDataValidator={validateQRCode}
                remoteQRCodeValidator={(text) => withTimeout(remoteQrCodeValidator(text), 1000)}
            />
        );

    if (!displayThisStep) return null;

    return (
        <Modal.Body>
            <div className="mb-2">
                <Typography variant="label">{headingLabel}</Typography>
            </div>

            <div style={{ display: 'flex', gap: '24px' }}>
                {/* Upload / Drag & Drop */}
                <div className={classes.attachments}>
                    <div
                        style={styles.optionsWrapper}
                        id={dropZoneId}
                        ref={ref}
                        onClick={() => {
                            uploadElementRef?.current?.click();
                        }}
                    >
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            ref={uploadElementRef}
                            onChange={qrCodeFileHandler}
                            data-testid="hidden-file-input"
                        />
                        <Icon name={'tray-arrow-up'} color="blueOneActive" />
                        <Typography variant="caption">Click to upload or Drag and drop </Typography>
                        <Typography variant="caption2">Jpeg, Pdf, doc or exe</Typography>
                    </div>
                </div>

                {/* Web Camera scanner */}
                <div className={classes.attachments}>
                    <div
                        style={styles.optionsWrapper}
                        onClick={() => {
                            checkCameraPermission().then((response) => {
                                if (response === 'denied') {
                                    uploadElementRef?.current?.click();
                                } else {
                                    setDisplayQrCodeScanner(true);
                                }
                            });
                        }}
                    >
                        <Icon name={'qrcode-scan'} color="blueOneActive" />
                        <Typography variant="caption">Click to scan the QR code</Typography>
                        <Typography variant="caption2">Web camera scan</Typography>
                    </div>
                </div>
            </div>

            {/* success card */}
            {scannerData && !fileQrValidationInProgress && (
                <div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px 16px',
                            border: '1px solid #999DA3',
                            borderRadius: '4px',
                            marginTop: '16px',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography variant="label">{resultLabel}</Typography>
                            <Typography variant="caption">{scannerData}</Typography>
                        </div>
                        <div onClick={() => clearSavedData({ abort: false, blankStrategy: 'undefined' })}>
                            <Icon name={successIcon.iconName} color={successIcon.colors} />
                        </div>
                    </div>
                </div>
            )}

            {/* error card */}
            {errorMessage && !fileQrValidationInProgress && (
                <div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px 16px',
                            border: '1px solid #999DA3',
                            borderRadius: '4px',
                            marginTop: '16px',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography variant="label" color="redOne">
                                {errorMessage}
                            </Typography>
                        </div>
                    </div>
                </div>
            )}

            {/* file validation loader */}
            {fileQrValidationInProgress && (
                <div className={classes.loaderCard}>
                    <div className={classes.progressContainer}>
                        <div className={classes.progressBar}></div>
                    </div>
                    <div
                        className={classes.iconWrapper}
                        onClick={() => {
                            clearSavedData({ abort: true, blankStrategy: 'undefined' });
                        }}
                    >
                        <Icon name="x-circle" color="black" />
                    </div>
                </div>
            )}
        </Modal.Body>
    );
};

export default Step34Content;
